import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {withStyles} from '@material-ui/core/styles';
import SvgDone from '@material-ui/icons/Done';
import SvgCancel from '@material-ui/icons/Clear';

import leaflet from 'leaflet';

import {helper, api} from '../../common/common';
import ControlButton from '../../map/ControlButton';
import ZoomControl from '../../map/ZoomControl';
import SvgSatellite from '../../asset/svg/Satellite';

const styles = (theme) => {
    return {
        container: {
            position: 'absolute',
            top: 16,
            bottom: 16,
            left: 452,
            right: 16,
            zIndex: -2,
            textAlign: 'left',
        },
        backgroundImage: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            margin: 'auto',
            width: '50%',
            height: '50%',
            zIndex: -2,
            fill: theme.iconStyle.light.color,
        },
        backPane: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: -2,
            backgroundColor: theme.palette.common.transparent,
            '&$selected': {
                backgroundColor: theme.palette.common.black,
            }
        },
        pane: {
            width: '100%',
            height: '100%',
            zIndex: -1,
            backgroundColor: theme.palette.common.transparent,
        },
        zoomControl: {
            position: 'absolute',
            bottom: 16,
            right: 16,
        },
        controlGroup: {
            position: 'absolute',
            bottom: 16,
            left: 0,
            right: 0,
            marginLeft: 'auto',
            marginRight: 'auto',
            width: 96,
            height: 40,
        },
        nextButton: {
            position: 'absolute',
            top: 0,
            right: 0,
            marginLeft: 'auto',
            marginRight: 'auto',
            width: 40,
            height: 40,
            borderRadius: '50%',
        },
        cancelButton: {
            position: 'absolute',
            top: 0,
            left: 0,
            marginLeft: 'auto',
            marginRight: 'auto',
            width: 40,
            height: 40,
            borderRadius: '50%',
        },
        default: {},
        selected: {}
    };
}

class Map extends Component {
    componentDidMount = () => {
        this._map = leaflet.map('mapContainer', {
            zoom: 4,
            center: [2, 2],
            minZoom: 7,
            worldCopyJump: true,
            zoomControl: false,
            maxBoundsViscosity: 1.0,
            attributionControl: false,
            preferCanvas: true,
            zoomAnimation: true,
            zoomAnimationThreshold: 1,
            fadeAnimation: false,
            markerZoomAnimation: false,
        });

        this._imgOverlay = [];

        this._boundsObj = leaflet.geoJson(null, {
            style: this._boundsStyle,
            interactive: false,
        });

        this._boundsObj.addTo(this._map);

        this._map.dragging.disable();
        this._map.keyboard.disable();

        this._displayRegion();
    }

    shouldComponentUpdate = (nextProps, nextState) => {
        if (this.props.regionListTimestamp !== nextProps.regionListTimestamp ||
            this.props.regionIndex !== nextProps.regionIndex ||
            (!this.props.overlayInfo.uid && this.props.overlayInfo.uid !== nextProps.overlayInfo.uid)) {

            this._redrawRegion = true;
        }

        if (!nextProps.overlayInfo.uid) {
            this._redrawRegion = null;
        }

        return (this.props.overlayInfo.uid !== nextProps.overlayInfo.uid);
    }

    render = () => {
        this._displayRegion();
        this._displayOverlay();

        const {classes} = this.props;

        const selected = (this._map && this.props.overlayInfo.uid)

        const backPaneClassName = classNames(classes.backPane,
            (selected) ? classes.selected : classes.default);

        return (
            <div className={classes.container}>
                <SvgSatellite className={classes.backgroundImage}/>
                <div className={backPaneClassName}/>
                <div
                    className={classes.pane}
                    id='mapContainer'
                />
                {this._renderComponents()}
            </div>
        );
    }

    _renderComponents = () => {
        if (!this._map) {
            return null;
        }

        if (!this.props.overlayInfo.uid) {
            this._map.touchZoom.disable();
            this._map.doubleClickZoom.disable();
            this._map.scrollWheelZoom.disable();
            return null;
        }

        this._map.touchZoom.enable();
        this._map.doubleClickZoom.enable();
        this._map.scrollWheelZoom.enable();

        const {classes} = this.props;

        return (
            <div>
                <ZoomControl
                    className={classes.zoomControl}
                    map={this._map}
                />
                <div className={classes.controlGroup}>
                    <ControlButton
                        className={classes.nextButton}
                        onClick={this._handleDone}
                    >
                        <SvgDone/>
                    </ControlButton>
                    <ControlButton
                        className={classes.cancelButton}
                        onClick={this._handleCancel}
                    >
                        <SvgCancel/>
                    </ControlButton>
                </div>
            </div>
        )
    }

    _displayRegion = () => {
        if (!this._map) {
            return;
        }

        if (!this.props.overlayInfo.uid) {
            this._boundsObj.clearLayers();
            return;
        }

        if (!this._redrawRegion) {
            return;
        }

        this._redrawRegion = null;

        const region = this.props.regionList[this.props.regionIndex];

        this._boundsObj.clearLayers();
        this._boundsObj.addData(region);

        const bounds = this._boundsObj.getBounds();
        const zoomLevel = this._map.getBoundsZoom(bounds, false);

        if (bounds.isValid()) {
            this._map.setMaxBounds(bounds);
            this._map.fitBounds(bounds, {
                maxZoom: zoomLevel - 2,
            });
        }

        this._map.setMaxZoom(zoomLevel + 1);
    }

    _displayOverlay = () => {
        if (!this._map) {
            return;
        }

        this._cleanupOverlay();

        if (!this.props.overlayInfo.uid) {
            return;
        }

        const missingLength = this.props.overlayInfo.content.length - this._imgOverlay.length;

        if (missingLength > 0) {
            for (var i = 0; i < missingLength; ++i) {
                const layer = leaflet.imageOverlay('', leaflet.LatLngBounds(), {
                    opacity: 1.0,
                    interactive: false,
                });

                layer.on({
                    error: (e) => {
                        this._processError(helper.makeError('Missing Image',
                            'Unable to load tasking imagery', 'missing_imagery'));
                    },
                });

                this._imgOverlay.push(layer);
            }
        }

        for (var index = 0; index < this.props.overlayInfo.content.length; ++index) {
            const info = this.props.overlayInfo.content[index];
            const img = this._imgOverlay[index];

            const path = api.tasking() +
                '/image?access_code=' + this.props.getAccessCode() +
                '&key=' + info.key;

            if (info.bounds.length !== 2) {
                continue;
            }

            if (img) {
                img.setUrl(path);
                img.setBounds(info.bounds);
                img.addTo(this._map);
            }
        }

        this._boundsObj.bringToFront();
    }

    _handleDone = () => {
        if (this.props.onDone) {
            this.props.onDone();
        }
    }

    _handleCancel = () => {
        if (this.props.onCancel) {
            this.props.onCancel();
        }
    }

    _cleanupOverlay = () => {
        for (var obj of this._imgOverlay) {
            obj.remove();
        }
    }

    _boundsStyle = (f) => {
        const {theme} = this.props;

        return {
            color: theme.palette.secondary.main,
            fillColor: theme.palette.secondary.main,
            weight: 2,
            dashArray: '0',
            opacity: 1.0,
            fillOpacity: 0.3,
        };
    }

    _processError = (error) => {
        if (this.props.onError) {
            this.props.onError(error);
        }
    }
}

Map.propTypes = {
    regionListTimestamp: PropTypes.number.isRequired,
    regionList: PropTypes.arrayOf(PropTypes.object).isRequired,
    regionIndex: PropTypes.number.isRequired,
    overlayInfo: PropTypes.object.isRequired,
    getAccessCode: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
    onDone: PropTypes.func,
    onError: PropTypes.func,
}

export default withStyles(styles, {withTheme: true})(Map);
